import React, { useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import { useContent } from "../hooks/useContent";
import { useLang } from "../context/LanguageContext";
import { SEO } from "../components/SEO";
import { PageHeader } from "../components/PageHeader";
import { ProjectCard } from "../components/cards/ProjectCard";
import { Stagger, StaggerItem, Reveal } from "../components/motion/Reveal";
import { CTASection } from "../components/home/FinancialBenefits";

const uniq = (list) => [...new Set(list.filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr"));
const selectCls = "h-11 rounded-full border border-charcoal/15 bg-white px-4 pe-9 text-sm text-charcoal outline-none focus:border-green min-w-0";

// Land bought and priced through the accounting panel, browsable by il / ilçe / mahalle.
export default function Portfolio() {
  const { t } = useLang();
  const { data, loading } = useContent("projects");
  const [status, setStatus] = useState("");
  const [province, setProvince] = useState("");
  const [district, setDistrict] = useState("");
  const [hood, setHood] = useState("");

  const items = useMemo(() => data.filter((p) => p.acc_property_id), [data]);
  const inProvince = (p) => !province || p.province === province;
  const inDistrict = (p) => !district || p.district === district;
  const provinces = uniq(items.map((p) => p.province));
  const districts = uniq(items.filter(inProvince).map((p) => p.district));
  const hoods = uniq(items.filter((p) => inProvince(p) && inDistrict(p)).map((p) => p.neighborhood));
  const shown = items.filter((p) => inProvince(p) && inDistrict(p) && (!hood || p.neighborhood === hood) && (!status || p.status === status));

  const pickProvince = (v) => { setProvince(v); setDistrict(""); setHood(""); };
  const pickDistrict = (v) => { setDistrict(v); setHood(""); };

  return (
    <>
      <SEO title={t.seo.portfolio.title} description={t.seo.portfolio.description} />
      <PageHeader
        overline={t.portfolio.overline}
        title={t.portfolio.title}
        sub={t.portfolio.sub}
        image="https://images.unsplash.com/photo-1473968512647-3e447244af8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
      />
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4 mb-8" data-testid="portfolio-filters">
              <div className="flex gap-2 overflow-x-auto no-scrollbar">
                {[["", t.portfolio.allStatuses], ["Available", t.portfolio.onSale], ["Coming Soon", t.portfolio.comingSoon], ["Sold", t.portfolio.sold]].map(([v, label]) => (
                  <button key={v || "all"} onClick={() => setStatus(v)} data-testid={`portfolio-status-${v || "all"}`}
                    className={`shrink-0 h-11 px-5 rounded-full text-sm font-semibold border transition-colors ${status === v ? "bg-green text-white border-green" : "bg-white text-charcoal/65 border-charcoal/15 hover:border-green"}`}>
                    {label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 lg:ms-auto">
                <select value={province} onChange={(e) => pickProvince(e.target.value)} className={selectCls} data-testid="portfolio-province" aria-label={t.land.province}>
                  <option value="">{t.portfolio.allProvinces}</option>
                  {provinces.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                <select value={district} onChange={(e) => pickDistrict(e.target.value)} className={selectCls} data-testid="portfolio-district" aria-label={t.land.district}>
                  <option value="">{t.portfolio.allDistricts}</option>
                  {districts.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
                <select value={hood} onChange={(e) => setHood(e.target.value)} className={`${selectCls} col-span-2 sm:col-span-1`} data-testid="portfolio-neighborhood" aria-label={t.land.neighborhood}>
                  <option value="">{t.portfolio.allNeighborhoods}</option>
                  {hoods.map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
          </Reveal>

          {!loading && items.length > 0 && (
            <p className="text-charcoal/50 text-sm mb-4 flex items-center gap-2" data-testid="portfolio-count">
              <MapPin className="w-4 h-4 text-gold" /> {t.portfolio.count.replace("{n}", shown.length)}
            </p>
          )}

          <Stagger className="grid grid-cols-2 lg:grid-cols-3 gap-0">
            {shown.map((p, i) => (
              <StaggerItem key={p.id || p.slug}>
                <ProjectCard project={p} index={i} />
              </StaggerItem>
            ))}
          </Stagger>
          {loading && <p className="text-center text-charcoal/50 py-20" data-testid="portfolio-loading">{t.common.loading}</p>}
          {!loading && !shown.length && <p className="text-center text-charcoal/50 py-20" data-testid="portfolio-empty">{t.portfolio.empty}</p>}
        </div>
      </section>
      <CTASection />
    </>
  );
}
